import React, { useState, useEffect, useRef, useCallback } from 'react';

interface VariosModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (nombre: string, valor: number) => void;
}

const VariosModal: React.FC<VariosModalProps> = ({ isOpen, onClose, onSave }) => {
  const [nombre, setNombre] = useState('');
  const [valor, setValor] = useState('');
  const [error, setError] = useState('');
  
  const nombreInputRef = useRef<HTMLInputElement | null>(null);
  const valorInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setNombre('');
      setValor('');
      setError('');
      // Enfocar el campo de nombre al abrir
      setTimeout(() => {
        if (nombreInputRef.current) {
          nombreInputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

  // CAMBIO: Convertir handleSubmit a useCallback
  const handleSubmit = useCallback(() => {
    if (!nombre.trim()) {
      setError('El nombre es requerido');
      return;
    }
    
    const valorNumerico = parseFloat(valor.replace(/[^\d]/g, ''));
    if (!valorNumerico || valorNumerico <= 0) {
      setError('El valor debe ser mayor a 0');
      return;
    }

    onSave(nombre.trim(), valorNumerico);
    onClose();
  }, [nombre, valor, onSave, onClose]);

  // Atajos de teclado - AHORA handleSubmit está en las dependencias
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      // ESC para cerrar
      if (e.key === 'Escape') {
        onClose();
      }
      // F8 para cambiar entre campos
      else if (e.key === 'F8') {
        e.preventDefault();
        if (document.activeElement === nombreInputRef.current) {
          valorInputRef.current?.focus();
        } else {
          nombreInputRef.current?.focus();
        }
      }
      // SHIFT para agregar (si los campos están completos)
      else if (e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        if (nombre.trim() && valor && parseFloat(valor.replace(/[^\d]/g, '')) > 0) {
          handleSubmit();
        }
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, nombre, valor, onClose, handleSubmit]); // CAMBIO: Agregué handleSubmit

  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    setValor(value);
    setError('');
  };

  const formatValor = (value: string) => {
    if (!value) return '';
    return parseInt(value).toLocaleString();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Shift') {
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className="fas fa-bolt text-white text-lg"></i>
              </div>
              <h2 className="text-xl font-bold text-white">Venta Rápida</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="space-y-4">
            {/* Campo Nombre */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-tag mr-2 text-blue-600"></i>
                Nombre del producto
              </label>
              <input
                type="text"
                value={nombre}
                onChange={(e) => {
                  setNombre(e.target.value);
                  setError('');
                }}
                onKeyPress={handleKeyPress}
                placeholder="Ej: Dulces, Galletas, etc."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                ref={nombreInputRef}
              />
            </div>

            {/* Campo Valor */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-dollar-sign mr-2 text-blue-600"></i>
                Valor del producto
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  $
                </span>
                <input
                  type="text"
                  value={formatValor(valor)}
                  onChange={handleValorChange}
                  onKeyPress={handleKeyPress}
                  placeholder="0"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-right font-medium"
                  ref={valorInputRef}
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <i className="fas fa-exclamation-circle text-red-500"></i>
                <span className="text-sm text-red-600 font-medium">{error}</span>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex gap-2">
                <i className="fas fa-info-circle text-blue-600 mt-0.5"></i>
                <p className="text-sm text-blue-700">
                  Este producto no afectará el inventario. Ideal para ventas rápidas de productos ocasionales.
                </p>
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg"
            >
              <i className="fas fa-check mr-2"></i>
              Agregar
            </button>
          </div>
        </div>

        {/* Guía de atajos de teclado */}
        <div className="px-6 py-3 bg-blue-50 border-t border-blue-200 flex justify-center gap-6">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-blue-600 text-white text-xs font-bold rounded">F8</span>
            <span className="text-sm text-blue-700">Foco</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-blue-600 text-white text-xs font-bold rounded">ESC</span>
            <span className="text-sm text-blue-700">Cancelar</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-blue-600 text-white text-xs font-bold rounded">SHIFT</span>
            <span className="text-sm text-blue-700">Agregar</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariosModal;